import { array, CommunicationStatus, compactFlatten, failure, missing, pending, success } from '@art-suite/art-core-ts';
import { useMemo } from 'react';

import { useQueryCache, useQueryCacheMutation } from '@kaiizen/react-persistent-cache';

export const useCreateUpdateMutatorsWithQueryCache = <RecordTypeCreate, RecordTypeUpdate, RecordType extends { id: string }>({
  apiCreate,
  apiUpdate,
  pluralKeyPrefix,
  onRecordCreateOrUpdate,
  getSingleKey,
}: {
  apiCreate: (record: RecordTypeCreate) => Promise<RecordType>;
  apiUpdate: (id: string, updates: RecordTypeUpdate) => Promise<RecordType>;
  pluralKeyPrefix?: string;
  onRecordCreateOrUpdate?: (record: RecordType) => void;
  getSingleKey: (key: string) => string;
}) => {
  const queryCache = useQueryCache<RecordType>();
  const onSuccess = async (record: RecordType) => {
    onRecordCreateOrUpdate?.(record);

    await queryCache.cache.set(getSingleKey(record.id), record);

    // update all queries that might contain this record
    if (pluralKeyPrefix) {
      await queryCache.refetchAll({ prefix: pluralKeyPrefix });
    }
  };

  return {
    create: useQueryCacheMutation(
      async (record: RecordTypeCreate): Promise<RecordType> => apiCreate(record),
      { onSuccess },
    ).mutate,
    update: useQueryCacheMutation(
      async (id: string, updates: RecordTypeUpdate): Promise<RecordType> => apiUpdate(id, updates),
      { onSuccess },
    ).mutate,
  };
};

export const useUpdateMutatorsWithQueryCache = <RecordTypeCreate, RecordTypeUpdate, RecordType extends { id: string }>({
  apiUpdate,
  pluralKeyPrefix,
  onRecordCreateOrUpdate,
  getSingleKey,
}: {
  apiUpdate: (id: string, updates: RecordTypeUpdate) => Promise<RecordType>;
  pluralKeyPrefix?: string;
  onRecordCreateOrUpdate?: (record: RecordType) => void;
  getSingleKey: (key: string) => string;
}) => {
  const queryCache = useQueryCache<RecordType>();
  const onSuccess = async (record: RecordType) => {
    onRecordCreateOrUpdate?.(record);

    await queryCache.cache.set(getSingleKey(record.id), record);

    // update all queries that might contain this record
    if (pluralKeyPrefix) {
      await queryCache.refetchAll({ prefix: pluralKeyPrefix });
    }
  };

  return {
    update: useQueryCacheMutation(
      async (id: string, updates: RecordTypeUpdate): Promise<RecordType> => apiUpdate(id, updates),
      { onSuccess },
    ).mutate,
  };
};

export interface StandardReactQueryStatusInfo {
  error?: Error | undefined;
  isLoading: boolean;
  isReady: boolean;
  isError: boolean;
  isMissing: boolean;
  errorInfo?: string | undefined;
  status: CommunicationStatus;
  progress: number;
  refetch: () => Promise<void>;
}

export const getStatusFromBooleanFlags = (isLoading: boolean, isError: boolean, isMissing: boolean, isReady: boolean): CommunicationStatus => {
  return isMissing
    ? missing
    : isError
    ? failure
    : isLoading || !isReady
    ? pending
    : success;
};

export const getStandardReactQueryStatusFromParts = ({
  isLoading,
  error,
  isMissing,
  isReady,
  refetch,
}: {
  isLoading: boolean;
  error?: Error | undefined;
  isMissing: boolean;
  isReady: boolean;
  refetch?: () => Promise<void>;
}): StandardReactQueryStatusInfo => {
  return {
    status: getStatusFromBooleanFlags(isLoading, !!error, isMissing, isReady),
    progress: isReady ? 1 : 0,
    isLoading,
    isReady,
    isError: !!error,
    isMissing,
    error,
    refetch: refetch ?? (() => Promise.resolve()),
  };
};

const combineStandardReactQueryStatusInfoCore = (...statusInfos: StandardReactQueryStatusInfo[]) => {
  const isLoading = statusInfos.some(s => s.isLoading),
    isError = statusInfos.some(s => s.isError),
    isMissing = statusInfos.some(s => s.isMissing),
    isReady = statusInfos.every(s => s.isReady),
    errorInfo = isError ? compactFlatten(statusInfos.map(s => s.errorInfo)).join(', ') : undefined;

  const sumProgress = statusInfos.reduce((a, b) => a + b.progress, 0);
  return {
    error: statusInfos.find(s => s.error) as Error | undefined,
    status: getStatusFromBooleanFlags(isLoading, isError, isMissing, isReady),
    progress: sumProgress / statusInfos.length,
    isLoading,
    isReady,
    isError,
    isMissing,
    errorInfo,
  };
};

const combineStandardReactQueryStatusInfoRefetch = (...statusInfos: StandardReactQueryStatusInfo[]) => {
  return () => Promise.all(statusInfos.map(s => s.refetch())).then(compactFlatten).then(() => undefined);
};

/** @deprecated use getCombinedCachedQueryStatus instead */
export const combineStandardReactQueryStatusInfo = (...statusInfos: StandardReactQueryStatusInfo[]): StandardReactQueryStatusInfo => {
  return {
    ...combineStandardReactQueryStatusInfoCore(...statusInfos),
    refetch: combineStandardReactQueryStatusInfoRefetch(...statusInfos),
  };
};

/** @deprecated use useCombinedCachedQueryStatus instead */
export const useCombineStandardReactQueryStatusInfo = (...statusInfos: StandardReactQueryStatusInfo[]): StandardReactQueryStatusInfo => {
  return {
    ...combineStandardReactQueryStatusInfoCore(...statusInfos),
    refetch: useMemo(() => combineStandardReactQueryStatusInfoRefetch(...statusInfos), statusInfos.map(s => s.refetch)),
  };
};

/** @deprecated use getCombinedCachedQueryStatusNamed instead */
export const combineStandardReactQueryStatusInfoNamed = (statusInfos: Record<string, StandardReactQueryStatusInfo>): StandardReactQueryStatusInfo => {
  const base = combineStandardReactQueryStatusInfo(...Object.values(statusInfos));
  if (base.errorInfo) {
    base.errorInfo = array(statusInfos, {
      when: v => v.error,
      with: (v: StandardReactQueryStatusInfo, k: string) => k + ': ' + v.errorInfo,
    }).join(', ');
  }
  return base;
};
